import {useCallback} from "react";
import * as Sentry from "@sentry/react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "../../../app/hooks";
import {KAIResponseType} from "../../../types";
import {
    addMessage,
    createThread,
    finishAssistantMessage,
    selectActiveStreamCount,
    setActiveThread,
    setEphemeralSearch,
    setStreamId,
} from "../threadsSlice";
import {chatApi} from "../chatApi";
import {
    createAssistantMessage,
    createUserMessage,
} from "../utils/messageFactories";
import {mapToDirectSearch} from "../utils/searchResultMappers";
import {
    couldBeTriggerSearch,
    isTriggerWordOnly,
    withTimeout,
} from "../utils/triggerWordUtils";

const systemName = "DIB";
const MAX_CONCURRENT_STREAMS = 3;

async function checkIsTriggerSearch(
    message: string,
    dispatch: ReturnType<typeof useAppDispatch>
): Promise<boolean> {
    if (!couldBeTriggerSearch(message)) return false;

    const triggerCheck = dispatch(
        chatApi.endpoints.containTriggerWord.initiate(message)
    );
    try {
        const triggerWords = await withTimeout(triggerCheck.unwrap());
        return isTriggerWordOnly(message, triggerWords);
    } catch {
        return false;
    } finally {
        triggerCheck.unsubscribe();
    }
}

export function useSplashSubmit() {
    const {t} = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [sendMessage] = chatApi.endpoints.sendMessage.useMutation();
    const [triggerSearchMutation] =
        chatApi.endpoints.triggerSearch.useMutation();
    const chosenResponseType = useAppSelector(
        (state) => state.session.chosenResponseType
    );
    const activeStreamCount = useAppSelector(selectActiveStreamCount);

    const startTriggerSearch = useCallback(
        async (entityId: string, message: string): Promise<boolean> => {
            try {
                const results = await triggerSearchMutation({
                    query: message,
                }).unwrap();
                if (!results?.length) return false;

                const directResults = results.map(mapToDirectSearch);
                dispatch(
                    setEphemeralSearch({
                        id: entityId,
                        ephemeralSearch: {
                            query: message,
                            results: directResults,
                        },
                    })
                );

                dispatch(
                    addMessage({
                        id: entityId,
                        message: createAssistantMessage(1, {
                            artifacts: [
                                {
                                    id: `search-${Date.now()}`,
                                    type: "search_results",
                                    schemaVersion: "1.0",
                                    source: "system",
                                    payload: {
                                        query: message,
                                        results: directResults,
                                    },
                                    createdAt: new Date().toISOString(),
                                },
                            ],
                        }),
                    })
                );

                dispatch(
                    addMessage({
                        id: entityId,
                        message: createAssistantMessage(2, {
                            content: JSON.stringify({content: ""}),
                            responseType: KAIResponseType.Fast,
                        }),
                    })
                );

                setTimeout(() => {
                    dispatch(
                        finishAssistantMessage({
                            id: entityId,
                            text: JSON.stringify({
                                content: t("chat.ephemeral_search.nudge"),
                            }),
                        })
                    );
                }, 1000);

                return true;
            } catch (error) {
                console.error("Trigger search failed:", error);
                Sentry.captureException(error, {
                    tags: {feature: "chat.splash-search"},
                });
                return false;
            }
        },
        [triggerSearchMutation, dispatch, t]
    );

    const startCompletion = useCallback(
        (entityId: string, message: string) => {
            dispatch(
                addMessage({
                    id: entityId,
                    message: createAssistantMessage(1, {
                        responseType: chosenResponseType,
                    }),
                })
            );
            dispatch(setStreamId({id: entityId, streamId: entityId}));

            sendMessage({
                system: {name: systemName},
                streamId: entityId,
                message,
                onThreadIdReceived: (threadId) => {
                    // Only swap the URL if the user is still on this thread's
                    // local path. If they've navigated away (e.g. started a
                    // second thread), don't yank them back.
                    if (window.location.pathname === `/chat/${entityId}`) {
                        navigate(`/chat/${threadId}`, {
                            replace: true,
                            state: {from: "/"},
                        });
                    }
                },
            });
        },
        [dispatch, sendMessage, chosenResponseType, navigate]
    );

    const handleSendMessage = useCallback(
        async (message: string) => {
            if (activeStreamCount >= MAX_CONCURRENT_STREAMS) {
                return;
            }

            const entityId = crypto.randomUUID();
            dispatch(
                createThread({
                    id: entityId,
                    streamId: entityId,
                    initialMessages: [createUserMessage(message, 0)],
                })
            );
            dispatch(setActiveThread(entityId));
            navigate(`/chat/${entityId}`);

            const isTrigger = await checkIsTriggerSearch(message, dispatch);
            if (isTrigger && (await startTriggerSearch(entityId, message)))
                return;

            startCompletion(entityId, message);
        },
        [
            dispatch,
            navigate,
            startTriggerSearch,
            startCompletion,
            activeStreamCount,
        ]
    );

    return handleSendMessage;
}
